/**
 * Frozen colour palette and quadrant classification.
 *
 * These exact hex values are shared by the on-screen chart and the PNG export,
 * so they must not drift.
 */

/**
 * Colours shared by the chart and the exported image.
 *
 * The red (`authLeft`) and green (`libLeft`) shades are deliberately deeper than
 * a decorative palette would be, because the white quadrant descriptor text sits
 * directly on these fills and has to clear WCAG AA 4.5:1 against white.
 * `#c0392b` reaches 5.44:1 and `#2f7d40` reaches 5.09:1. Their predecessors
 * managed only 4.08:1 and 3.49:1 at full opacity, and 3.35:1 and 2.83:1 once the
 * quadrant rects were painted at 0.85 opacity over the white page — which is why
 * the fills are now drawn at full opacity as well. Do not lighten these or
 * reintroduce a translucent fill without re-checking the ratio; the dark halo on
 * the descriptor text is a second line of defence, not a licence to go pale.
 */
export const PALETTE = {
  authLeft: '#c0392b', // x<0, y>0  (red, 5.44:1 vs white)
  authRight: '#2f62c4', // x>0, y>0  (blue)
  libLeft: '#2f7d40', // x<0, y<0  (green, 5.09:1 vs white)
  libRight: '#8a4baf', // x>0, y<0  (purple)
  grid: '#1f2933',
  axisText: '#0f172a',
  quadrantText: '#ffffff',
  pointFill: '#ffffff',
  pointStroke: '#0f172a',
  trajectory: '#0f172a',
  plotBackground: '#ffffff',
} as const

/** The four quadrants, keyed by the palette colour that fills them. */
export type QuadrantKey = 'authLeft' | 'authRight' | 'libLeft' | 'libRight'

/**
 * Classify a compass coordinate into its quadrant.
 *
 * Points ON an axis (x === 0 and/or y === 0) are ambiguous, so this function
 * picks deterministically: the boundary belongs to the positive side of each
 * axis, i.e. `x >= 0, y >= 0` is `authRight`. That makes (0, 0) authRight,
 * (0, 5) authRight, (0, -5) libRight and (-5, 0) libLeft.
 */
export function quadrantOf(x: number, y: number): QuadrantKey {
  if (y >= 0) return x >= 0 ? 'authRight' : 'authLeft'
  return x >= 0 ? 'libRight' : 'libLeft'
}

/** Human-readable label for each quadrant. */
export const QUADRANT_LABELS: Record<QuadrantKey, string> = {
  authLeft: 'Authoritarian Left',
  authRight: 'Authoritarian Right',
  libLeft: 'Libertarian Left',
  libRight: 'Libertarian Right',
}

/** Labels drawn on the four compass poles. */
export const AXIS_LABELS = {
  top: 'Authoritarian',
  bottom: 'Libertarian',
  left: 'Economic Left',
  right: 'Economic Right',
} as const
